import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  AttachMoney as SalaryIcon,
  DateRange as DateIcon,
  VisibilityOff as DesativarIcon,
  Visibility as AtivarIcon,
  CheckCircle as AtivasIcon,
  Cancel as InativasIcon,
  RemoveRedEye as VisualizarIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { buscarVagas } from "../../services/vaga";
import { Vaga } from "../../types";
import { normalizarNomeEmpresa } from "../../utils/normalization";
import axiosInstance from "../../services/api/axiosConfig";
import { getApiBaseUrl } from "../../services/baseUrl";
import { formatarData as formatarDataBackend } from "../../utils/data";

const ListarVagasPrefeitura: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [modalDesativar, setModalDesativar] = useState(false);
  const [vagaParaDesativar, setVagaParaDesativar] = useState<Vaga | null>(null);

  const [filtroStatus, setFiltroStatus] = useState<"ativas" | "inativas">(
    "ativas",
  );

  const [todasVagas, setTodasVagas] = useState<Vaga[]>([]);

  const [modalDetalhes, setModalDetalhes] = useState(false);
  const [vagaSelecionada, setVagaSelecionada] = useState<Vaga | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const carregarVagas = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      // Buscar TODAS as vagas (ativas e inativas) e empresas ativas em paralelo
      const [vagasData, empresasResponse] = await Promise.all([
        buscarVagas(false), // false = buscar todas as vagas, não só ativas
        axiosInstance.get(`${getApiBaseUrl()}/empresas`),
      ]);

      const vagasProcessadas = Array.isArray(vagasData) ? vagasData : [];

      // Obter lista de IDs de empresas ativas
      const empresasArray = Array.isArray(empresasResponse.data)
        ? empresasResponse.data
        : empresasResponse.data?.data || [];

      const empresasAtivas = empresasArray
        .filter((empresa: any) => empresa.ativo === "S")
        .map((empresa: any) => empresa.id);

      // Filtrar vagas de empresas ativas e ordenar
      const vagasDeEmpresasAtivas = vagasProcessadas
        .filter((vaga) => {
          const empresaId = vaga.idEmpresa || vaga.id_empresa?.id;
          return empresasAtivas.includes(empresaId);
        })
        .sort((a, b) => Number(b.id) - Number(a.id)); // Ordenar por ID (mais recentes primeiro)

      // Guardar todas as vagas para filtros
      setTodasVagas(vagasDeEmpresasAtivas);

      // Aplicar filtro de status
      aplicarFiltroStatus(vagasDeEmpresasAtivas, filtroStatus);
    } catch (error) {
      console.error("Erro ao carregar vagas:", error);
      setError("Erro ao carregar vagas. Tente novamente.");
      setVagas([]);
      setTodasVagas([]);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => {
    carregarVagas();
  }, [carregarVagas]);

  useEffect(() => {
    aplicarFiltroStatus(todasVagas, filtroStatus);
  }, [filtroStatus, todasVagas]);

  const showMessage = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success",
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleDesativarVaga = async (vaga: Vaga) => {
    if (!vaga || !vaga.id) {
      showMessage("Erro: Vaga inválida.", "error");
      return;
    }

    try {
      // Atualizar vaga para inativo
      const dadosAtualizacao = {
        ativo: false, // Campo boolean esperado pelo backend
      };

      const response = await axiosInstance.put(
        `${getApiBaseUrl()}/vagas/${vaga.id}`,
        dadosAtualizacao,
      );

      if (response.data.success) {
        // Atualizar lista local e filtrar novamente
        const vagasAtualizadas = todasVagas.map((v: Vaga) =>
          v.id === vaga.id ? { ...v, ativo: false } : v,
        );
        setTodasVagas(vagasAtualizadas);
        aplicarFiltroStatus(vagasAtualizadas, filtroStatus);

        showMessage("Vaga desativada com sucesso!", "success");
        setModalDesativar(false);
        setVagaParaDesativar(null);
      } else {
        throw new Error(response.data.message || "Erro ao desativar vaga");
      }
    } catch (error: any) {
      console.error("Erro ao desativar vaga:", error);
      const mensagem =
        error?.response?.data?.message ||
        error?.message ||
        "Erro desconhecido ao desativar vaga";
      showMessage(`Erro ao desativar vaga: ${mensagem}`, "error");
    }
  };

  const handleAtivarVaga = async (vaga: Vaga) => {
    if (!vaga || !vaga.id) {
      showMessage("Erro: Vaga inválida.", "error");
      return;
    }

    try {
      // Atualizar vaga para ativo
      const dadosAtualizacao = {
        ativo: true, // Campo boolean esperado pelo backend
      };

      const response = await axiosInstance.put(
        `${getApiBaseUrl()}/vagas/${vaga.id}`,
        dadosAtualizacao,
      );

      if (response.data.success) {
        const vagasAtualizadas = todasVagas.map((v: Vaga) =>
          v.id === vaga.id ? { ...v, ativo: true } : v,
        );
        setTodasVagas(vagasAtualizadas);
        aplicarFiltroStatus(vagasAtualizadas, filtroStatus);

        showMessage("Vaga ativada com sucesso!", "success");
      } else {
        throw new Error(response.data.message || "Erro ao ativar vaga");
      }
    } catch (error: any) {
      console.error("Erro ao ativar vaga:", error);
      const mensagem =
        error?.response?.data?.message ||
        error?.message ||
        "Erro desconhecido ao ativar vaga";
      showMessage(`Erro ao ativar vaga: ${mensagem}`, "error");
    }
  };

  const aplicarFiltroStatus = (
    vagasParaFiltrar: Vaga[],
    status: "ativas" | "inativas",
  ) => {
    const vagasFiltradas = status === "ativas"
      ? vagasParaFiltrar.filter((vaga) => vaga.ativo)
      : vagasParaFiltrar.filter((vaga) => !vaga.ativo);
    
    setVagas(vagasFiltradas);
    setPage(0); // Resetar página
  };

  const handleChangeFiltroStatus = (novoStatus: "ativas" | "inativas") => {
    setFiltroStatus(novoStatus);
    aplicarFiltroStatus(todasVagas, novoStatus);
  };

  const abrirModalDesativacao = (vaga: Vaga) => {
    setVagaParaDesativar(vaga);
    setModalDesativar(true);
  };

  const fecharModalDesativacao = () => {
    setModalDesativar(false);
    setVagaParaDesativar(null);
  };

  const abrirModalDetalhes = (vaga: Vaga) => {
    setVagaSelecionada(vaga);
    setModalDetalhes(true);
  };

  const fecharModalDetalhes = () => {
    setModalDetalhes(false);
    setVagaSelecionada(null);
  };

  const filtrarVagas = (vagas: Vaga[]) => {
    return vagas.filter(
      (vaga) =>
        vaga.nomeVaga?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vaga.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vaga.descricaoVaga?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vaga.nomeEmpresa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vaga.id_empresa?.nome?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const vagasFiltradas = filtrarVagas(vagas);

  const formatarData = (data?: string) =>
    formatarDataBackend(data, "Não informado");

  const formatarSalario = (salario?: number | string) => {
    if (!salario || salario === 0) return "Não informado";
    const valor = typeof salario === "string" ? parseFloat(salario) : salario;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const renderCardsMobile = (vagas: Vaga[]) => {
    const vagasPaginadas = vagas.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    return (
      <Box>
        {vagasPaginadas.map((vaga) => (
          <Card key={vaga.id} sx={{ mb: 2 }} elevation={3}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BusinessIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {normalizarNomeEmpresa(
                      vaga.nomeEmpresa || vaga.id_empresa?.nome || "Empresa não identificada",
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WorkIcon fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {vaga.nomeVaga || "Vaga não identificada"}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  <strong>Cargo:</strong> {vaga.cargo || "Não especificado"}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SalaryIcon fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "success.main" }}
                  >
                    {formatarSalario(vaga.salario)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DateIcon fontSize="small" sx={{ opacity: 0.7 }} />
                  <Typography variant="body2">
                    Criada em: {formatarData(vaga.dataCriacao)}
                  </Typography>
                </Box>
              </Box>

              {/* Botões de ação */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                  flexWrap: "wrap",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => abrirModalDetalhes(vaga)}
                  sx={{
                    color: "info.main",
                    "&:hover": {
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                    },
                  }}
                  title="Visualizar detalhes"
                >
                  <VisualizarIcon fontSize="small" />
                </IconButton>
                
                {filtroStatus === "ativas" ? (
                  <IconButton
                    size="small"
                    onClick={() => abrirModalDesativacao(vaga)}
                    sx={{
                      color: "warning.main",
                      "&:hover": {
                        backgroundColor: "rgba(245, 124, 0, 0.1)",
                      },
                    }}
                    title="Desativar vaga"
                  >
                    <DesativarIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleAtivarVaga(vaga)}
                    sx={{
                      color: "success.main",
                      "&:hover": {
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                      },
                    }}
                    title="Ativar vaga"
                  >
                    <AtivarIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const renderTabelaDesktop = (vagas: Vaga[]) => {
    const vagasPaginadas = vagas.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    return (
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vaga</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cargo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Salário</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Data Criação</TableCell>
              <TableCell sx={{ fontWeight: 600, width: { xs: 100, sm: 140 } }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vagasPaginadas.map((vaga) => (
              <TableRow key={vaga.id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <BusinessIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {normalizarNomeEmpresa(
                        vaga.nomeEmpresa || vaga.id_empresa?.nome || "Empresa não identificada",
                      )}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={vaga.nomeVaga || "Vaga não identificada"}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {vaga.cargo || "Não especificado"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ color: "success.main", fontWeight: 500 }}
                  >
                    {formatarSalario(vaga.salario)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatarData(vaga.dataCriacao)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <IconButton
                      size="small"
                      onClick={() => abrirModalDetalhes(vaga)}
                      sx={{
                        color: "info.main",
                        "&:hover": {
                          backgroundColor: "rgba(33, 150, 243, 0.1)",
                        },
                      }}
                      title="Visualizar detalhes"
                    >
                      <VisualizarIcon fontSize="small" />
                    </IconButton>
                    
                    {filtroStatus === "ativas" ? (
                      <IconButton
                        size="small"
                        onClick={() => abrirModalDesativacao(vaga)}
                        sx={{
                          color: "warning.main",
                          "&:hover": {
                            backgroundColor: "rgba(245, 124, 0, 0.1)",
                          },
                        }}
                        title="Desativar vaga"
                      >
                        <DesativarIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleAtivarVaga(vaga)}
                        sx={{
                          color: "success.main",
                          "&:hover": {
                            backgroundColor: "rgba(76, 175, 80, 0.1)",
                          },
                        }}
                        title="Ativar vaga"
                      >
                        <AtivarIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Cabeçalho */}
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ fontWeight: 700, mb: 1 }}
      >
        Painel de Vagas - Prefeitura
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Visualização de todas as vagas cadastradas pelas empresas ativas
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Campo de Busca e Filtros */}
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        {/* Toggle para vagas ativas/inativas */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <ToggleButtonGroup
            value={filtroStatus}
            exclusive
            onChange={(_, novoStatus) =>
              novoStatus && handleChangeFiltroStatus(novoStatus)
            }
            aria-label="filtro de status das vagas"
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="ativas" aria-label="vagas ativas">
              <AtivasIcon sx={{ mr: 1 }} />
              Vagas Ativas
            </ToggleButton>
            <ToggleButton value="inativas" aria-label="vagas inativas">
              <InativasIcon sx={{ mr: 1 }} />
              Vagas Inativas
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder={
            isMobile
              ? "Buscar..."
              : "Buscar por vaga, cargo, descrição ou empresa..."
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: "text.secondary" }}
          >
            Vagas encontradas: {vagasFiltradas.length}
          </Typography>
        )}
      </Paper>

      {/* Lista de Vagas */}
      <Paper elevation={3}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {filtroStatus === "ativas" ? (
              <AtivasIcon sx={{ color: "success.main" }} />
            ) : (
              <InativasIcon sx={{ color: "error.main" }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Vagas {filtroStatus === "ativas" ? "Ativas" : "Inativas"} (
              {vagasFiltradas.length})
            </Typography>
          </Box>
        </Box>

        {vagasFiltradas.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <WorkIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchTerm
                ? "Nenhuma vaga encontrada para a busca"
                : "Nenhuma vaga cadastrada ainda"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {isMobile
              ? renderCardsMobile(vagasFiltradas)
              : renderTabelaDesktop(vagasFiltradas)}

            <TablePagination
              component={Paper}
              elevation={1}
              sx={{ mt: 2 }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={vagasFiltradas.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={isMobile ? "Por página:" : "Linhas por página:"}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </Box>
        )}
      </Paper>

      {/* Modal de detalhes da vaga */}
      <Dialog
        open={modalDetalhes}
        onClose={fecharModalDetalhes}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VisualizarIcon color="primary" />
              <Typography variant="h6">Detalhes da Vaga</Typography>
            </Box>
            <IconButton onClick={fecharModalDetalhes} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {vagaSelecionada && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Informações da Empresa */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  <BusinessIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                  Empresa
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {normalizarNomeEmpresa(
                    vagaSelecionada.nomeEmpresa || vagaSelecionada.id_empresa?.nome || "Empresa não identificada"
                  )}
                </Typography>
              </Box>

              {/* Informações da Vaga */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  <WorkIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                  Nome da Vaga
                </Typography>
                <Typography variant="body1">
                  {vagaSelecionada.nomeVaga || "Não informado"}
                </Typography>
              </Box>

              {/* Cargo */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Cargo
                </Typography>
                <Typography variant="body1">
                  {vagaSelecionada.cargo || "Não especificado"}
                </Typography>
              </Box>

              {/* Salário */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  <SalaryIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                  Salário
                </Typography>
                <Typography variant="body1" sx={{ color: "success.main", fontWeight: 500 }}>
                  {formatarSalario(vagaSelecionada.salario)}
                </Typography>
              </Box>

              {/* Descrição */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Descrição
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "grey.50",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.6,
                    }}
                  >
                    {vagaSelecionada.descricaoVaga || "Descrição não informada"}
                  </Typography>
                </Paper>
              </Box>

              {/* Data de Criação */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  <DateIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                  Data de Criação
                </Typography>
                <Typography variant="body1">
                  {formatarData(vagaSelecionada.dataCriacao)}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Status
                </Typography>
                <Chip
                  label={vagaSelecionada.ativo ? "Ativa" : "Inativa"}
                  color={vagaSelecionada.ativo ? "success" : "default"}
                  size="small"
                  icon={vagaSelecionada.ativo ? <AtivasIcon /> : <InativasIcon />}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={fecharModalDetalhes}
            variant="contained"
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de desativação */}
      <Dialog
        open={modalDesativar}
        onClose={fecharModalDesativacao}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DesativarIcon color="warning" />
            Confirmar Desativação
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja desativar a vaga{" "}
            <strong>"{vagaParaDesativar?.nomeVaga}"</strong> da empresa{" "}
            <strong>"{vagaParaDesativar?.id_empresa?.nome}"</strong>?
            <br />
            <br />
            <strong>A vaga será ocultada do painel público</strong> mas pode ser
            reativada posteriormente pela empresa.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={fecharModalDesativacao}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() =>
              vagaParaDesativar && handleDesativarVaga(vagaParaDesativar)
            }
            color="warning"
            variant="contained"
            autoFocus
            startIcon={<DesativarIcon />}
          >
            Desativar Vaga
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListarVagasPrefeitura;
